import { Box, CircularProgress, Grid, Paper, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Gauge from './Gauge'
import MetricCard from './MetricCard'

const CARD_MIN_HEIGHT = 116

const format = value => {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  if (Number.isInteger(n)) return n.toLocaleString()
  return Math.abs(n) >= 100 ? Math.round(n).toLocaleString() : String(Number(n.toFixed(2)))
}

const Detail = ({ detail }) => {
  const { t } = useTranslation()

  return (
    <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.35, color: 'text.primary' }}>
        {format(detail.value)}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        {t(detail.label ?? '')}
      </Typography>
    </Stack>
  )
}

const StatCard = ({ card, columns, variant }) => {
  const { t } = useTranslation()

  const details = Array.isArray(card.details) ? card.details.filter(Boolean) : []

  return (
    <Grid item xs={12} sm={6} md={columns}>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          p: 2,
          height: '100%',
          minHeight: CARD_MIN_HEIGHT,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          transition: 'box-shadow .15s, border-color .15s',
          '&:hover': { boxShadow: 2, borderColor: 'action.disabled' },
        }}
      >
        {variant === 'metric' ? (
          <MetricCard card={card} format={format} />
        ) : (
          <>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: details.length ? 1 : 0.5 }}
          >
            {t(card.label ?? '')}
          </Typography>

          {details.length > 0 ? (
            <Stack spacing={0.25}>
              {details.map((detail, index) => (
                <Detail key={detail.label ?? index} detail={detail} />
              ))}
            </Stack>
          ) : (
            card.caption && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {t(card.caption)}
              </Typography>
            )
          )}
        </Box>

        <Gauge
          value={card.value}
          max={card.max}
          unit={card.unit}
          display={format(card.value)}
          caption={details.length && card.caption ? t(card.caption) : card.gaugeLabel ? t(card.gaugeLabel) : null}
        />
          </>
        )}
      </Paper>
    </Grid>
  )
}

export default function StatCards({ cards = [], isReady = true, emptyMessage, columns, variant = 'gauge' }) {
  const { t } = useTranslation()

  if (!isReady) {
    return (
      <Grid container spacing={1}>
        {[0, 1, 2].map(index => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                minHeight: CARD_MIN_HEIGHT,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <CircularProgress size={22} />
            </Paper>
          </Grid>
        ))}
      </Grid>
    )
  }

  if (!cards.length) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 2.25, borderRadius: 2.5, border: '1px dashed', borderColor: 'divider', textAlign: 'center' }}
      >
        <Typography variant="body2" color="text.secondary">
          {emptyMessage ?? t('No data to report.')}
        </Typography>
      </Paper>
    )
  }

  const span = columns ?? Math.max(3, Math.floor(12 / cards.length))

  return (
    <Grid container spacing={0.5}>
      {cards.map((card, index) => (
        <StatCard key={card.label ?? index} card={card} columns={span} variant={variant} />
      ))}
    </Grid>
  )
}
